import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Plus,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  ChevronRight,
  Loader2,
  AlertTriangle,
  Search,
  Filter,
  Calendar,
  Car,
  DollarSign,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import PortalLayout from '../components/PortalLayout';
import { getUserFriendlyError } from '../utils/errorMessages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status icon mapping
const getStatusIcon = (status) => {
  const icons = {
    draft: FileText,
    submitted: Clock,
    under_review: Search,
    pending_documents: AlertCircle,
    in_appraisal: DollarSign,
    approved: CheckCircle,
    denied: XCircle,
    paid: CheckCircle,
    closed: FileText
  };
  return icons[status] || FileText;
};

// Status color mapping
const getStatusColor = (status) => {
  const colors = {
    draft: 'bg-slate-100 text-slate-600',
    submitted: 'bg-blue-100 text-blue-700',
    under_review: 'bg-yellow-100 text-yellow-700',
    pending_documents: 'bg-amber-100 text-amber-700',
    in_appraisal: 'bg-purple-100 text-purple-700',
    approved: 'bg-green-100 text-green-700',
    denied: 'bg-red-100 text-red-700',
    paid: 'bg-emerald-100 text-emerald-700',
    closed: 'bg-slate-100 text-slate-600'
  };
  return colors[status] || 'bg-slate-100 text-slate-600';
};

// Currency formatting
const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  if (num % 1 === 0) {
    return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Date formatting
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

export default function ClaimsListPage() {
  const navigate = useNavigate();
  const { getToken, logout } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [claims, setClaims] = useState([]);
  const [summary, setSummary] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userName, setUserName] = useState('');

  useEffect(() => {
    fetchClaimsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchClaimsData = async () => {
    const token = getToken();
    if (!token) {
      navigate('/portal/login');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch claims and summary in parallel
      const [claimsRes, summaryRes, dashboardRes] = await Promise.all([
        fetch(`${API_URL}/api/claims`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/claims/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/portal/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (claimsRes.status === 401 || summaryRes.status === 401) {
        await logout();
        navigate('/portal/login');
        return;
      }

      const claimsData = await claimsRes.json();
      const summaryData = await summaryRes.json();
      const dashboardData = await dashboardRes.json();

      if (claimsData.success) {
        setClaims(claimsData.claims);
      }
      if (summaryData.success) {
        setSummary(summaryData.summary);
      }
      if (dashboardData.policyholder) {
        setUserName(dashboardData.policyholder.first_name || 'Policyholder');
      }

    } catch (err) {
      setError(getUserFriendlyError(err.message));
    } finally {
      setLoading(false);
    }
  };

  // Filter claims based on search and status
  const filteredClaims = claims.filter(claim => {
    const matchesSearch = searchTerm === '' || 
      claim.claim_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.vehicle?.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.vehicle?.model?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || claim.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8EEFF] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#3B5BDB] mx-auto" />
          <p className="mt-4 text-slate-600 font-medium">Loading your claims...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#E8EEFF] flex items-center justify-center px-4">
        <Card className="max-w-md w-full shadow-xl border-0 rounded-2xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Something went wrong</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button 
              onClick={fetchClaimsData} 
              className="bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white rounded-xl"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <PortalLayout pageTitle="Claims" userName={userName}>
      <div className="bg-[#E8EEFF] min-h-full py-8 md:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 rounded-full text-sm font-medium text-[#3B5BDB] mb-4">
                <span className="w-2 h-2 bg-[#3B5BDB] rounded-full"></span>
                Claims Center
              </span>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
                Your Claims
              </h1>
              <p className="text-slate-600 mt-2">
                Track and manage your diminished value claims
              </p>
            </div>
            
            <Button
              onClick={() => navigate('/claims/file')}
              className="bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white rounded-xl h-12 px-6"
              data-testid="file-new-claim-btn"
            >
              <Plus className="h-5 w-5 mr-2" />
              File a Claim
            </Button>
          </div>

          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="shadow-lg border-0 rounded-xl">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                      <FileText className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{summary.total}</p>
                      <p className="text-sm text-slate-500">Total Claims</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="shadow-lg border-0 rounded-xl">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                      <Clock className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{summary.active}</p>
                      <p className="text-sm text-slate-500">Active</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="shadow-lg border-0 rounded-xl">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                      <AlertCircle className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{summary.pending}</p>
                      <p className="text-sm text-slate-500">Needs Action</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="shadow-lg border-0 rounded-xl">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{summary.resolved}</p>
                      <p className="text-sm text-slate-500">Resolved</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Search and Filter */}
          <Card className="shadow-xl border-0 rounded-2xl mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    placeholder="Search by claim number or vehicle..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-12 rounded-xl"
                    data-testid="claims-search-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-slate-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-12 px-4 rounded-xl border border-slate-200 text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B5BDB]"
                    data-testid="claims-status-filter"
                  >
                    <option value="all">All Statuses</option>
                    <option value="submitted">Submitted</option>
                    <option value="under_review">Under Review</option>
                    <option value="pending_documents">Pending Documents</option>
                    <option value="in_appraisal">In Appraisal</option>
                    <option value="approved">Approved</option>
                    <option value="denied">Denied</option>
                    <option value="paid">Paid</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Claims List */}
          {filteredClaims.length === 0 ? (
            <Card className="shadow-xl border-0 rounded-2xl">
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-8 w-8 text-slate-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {claims.length === 0 ? 'No Claims Yet' : 'No Matching Claims'}
                </h3>
                <p className="text-slate-600 mb-6 max-w-md mx-auto">
                  {claims.length === 0 
                    ? "You haven't filed any claims yet. If your vehicle was in an accident caused by another driver, you may be eligible for diminished value compensation."
                    : "No claims match your current search or filter criteria."
                  }
                </p>
                {claims.length === 0 && (
                  <Button
                    onClick={() => navigate('/claims/file')}
                    className="bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white rounded-xl"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    File Your First Claim
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredClaims.map((claim) => {
                const StatusIcon = getStatusIcon(claim.status);
                
                return (
                  <Card 
                    key={claim.claim_number}
                    className="shadow-lg border-0 rounded-2xl hover:shadow-xl transition-shadow cursor-pointer"
                    onClick={() => navigate(`/claims/${claim.claim_number}`)}
                    data-testid={`claim-card-${claim.claim_number}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        {/* Claim Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <p className="font-mono font-bold text-slate-900">
                              {claim.claim_number}
                            </p>
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(claim.status)}`}>
                              <StatusIcon className="h-3.5 w-3.5" />
                              {claim.status_label}
                            </span>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                            <span className="flex items-center gap-1.5">
                              <Car className="h-4 w-4" />
                              {claim.vehicle?.year} {claim.vehicle?.make} {claim.vehicle?.model}{claim.vehicle?.trim ? ` ${claim.vehicle.trim}` : ''}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <Calendar className="h-4 w-4" />
                              Accident: {formatDate(claim.accident_date)}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <DollarSign className="h-4 w-4" />
                              Coverage: {formatCurrency(claim.coverage_amount)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Arrow */}
                        <div className="flex items-center">
                          <ChevronRight className="h-5 w-5 text-slate-400" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Help Section */}
          <Card className="shadow-xl border-0 rounded-2xl mt-8 bg-gradient-to-br from-[#EFF6FF] to-[#E8EEFF]">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 mb-1">Need Help with Your Claim?</h3>
                  <p className="text-slate-600 text-sm">
                    Our claims team is available Monday-Friday, 9am-5pm EST to assist you.
                  </p>
                </div>
                <div className="flex gap-3">
                  <a 
                    href="tel:18335677729"
                    className="px-4 py-2 bg-white text-[#3B5BDB] rounded-xl font-medium hover:bg-slate-50 transition-colors"
                  >
                    1-833-LOSSPAY
                  </a>
                  <a 
                    href="mailto:claims@losspay.com"
                    className="px-4 py-2 bg-[#3B5BDB] text-white rounded-xl font-medium hover:bg-[#2F4BC7] transition-colors"
                  >
                    Email Support
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </PortalLayout>
  );
}
